import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  Input,
  Label,
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  withKey,
} from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { UserSearchCombobox } from "../../UserSearchCombobox";
import { X } from "lucide-react";
import { useAuth, useTellescopeMutation, useTellescopeQuery, useUsersQuery } from "@tellescope/tanstack-query";
import { useForm } from "@tellescope/forms";
import { useCallback, useMemo } from "react";
import { ALL_ENDUSER_FIELDS_TO_DISPLAY_NAME } from "@tellescope/constants";

export interface TicketQueueCreateEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  queueId?: string;
}

export const TicketQueueCreateEditSheet = withKey(
  ({ open, onOpenChange, queueId }: TicketQueueCreateEditSheetProps) => {
    const { t } = useTranslation("ticket-queues");
    const editMode = !!queueId;
    const { selectedOrganization: organization } = useAuth();

    const { data: queue } = useTellescopeQuery({
      model: "ticket_queues",
      method: "getOne",
      request: queueId ?? "",
      enabled: !!queueId,
    });

    const createQueueMutation = useTellescopeMutation({
      model: "ticket_queues",
      method: "createOne",
    });

    const updateQueueMutation = useTellescopeMutation({
      model: "ticket_queues",
      method: "updateOne",
    });

    const form = useForm({
      fields: {
        title: {
          type: "text",
          initialValue: queue?.title ?? "",
          required: true,
        },
        userIds: {
          type: "multiselect",
          initialValue: queue?.userIds ?? [],
        },
        preventPull: {
          type: "multiselect",
          initialValue: queue?.preventPull ?? [],
        },
        type: {
          type: "text",
          initialValue: queue?.type ?? "Default",
        },
        defaultFromNumber: {
          type: "text",
          initialValue: queue?.defaultFromNumber ?? "",
        },
        overdueReminderUserId: {
          type: "text",
          initialValue: queue?.overdueReminderUserId ?? "",
        },
        enduserFields: {
          type: "multiselect",
          initialValue: queue?.enduserFields ?? [],
        },
      },
      validateOn: "blur",
    });

    const isPending = createQueueMutation.isPending || updateQueueMutation.isPending;

    const handleSubmit = useCallback(async () => {
      if (editMode && queue) {
        await updateQueueMutation.mutateAsync({
          id: queue.id,
          title: form.values.title,
          userIds: form.values.userIds,
          preventPull: form.values.preventPull,
          type: form.values.type || undefined,
          defaultFromNumber: form.values.type === "Autodialer" ? form.values.defaultFromNumber || undefined : undefined,
          overdueReminderUserId: form.values.overdueReminderUserId || undefined,
          enduserFields: form.values.enduserFields,
          _options: {
            replaceObjectFields: true,
          },
        });
      } else {
        await createQueueMutation.mutateAsync({
          title: form.values.title,
          userIds: form.values.userIds,
          preventPull: form.values.preventPull,
          type: form.values.type || undefined,
          defaultFromNumber: form.values.type === "Autodialer" ? form.values.defaultFromNumber || undefined : undefined,
          overdueReminderUserId: form.values.overdueReminderUserId || undefined,
          enduserFields: form.values.enduserFields,
        });
      }
      form.resetForm();
      onOpenChange(false);
    }, [createQueueMutation, editMode, form, onOpenChange, queue, updateQueueMutation]);

    const { data: queriedUsers = [] } = useUsersQuery(form.values.userIds);
    const selectedUsers = form.values.userIds.length ? queriedUsers : [];

    const { data: queriedPreventPullUsers = [] } = useUsersQuery(form.values.preventPull);
    const selectedPreventPullUsers = form.values.preventPull.length ? queriedPreventPullUsers : [];

    const { data: queriedReminderUsers = [] } = useUsersQuery(
      form.values.overdueReminderUserId ? [form.values.overdueReminderUserId] : []
    );
    const selectedReminderUser = form.values.overdueReminderUserId ? queriedReminderUsers : [];

    const phoneOptions = useMemo(() => {
      const labels = organization?.phoneLabels ?? [];
      return labels.map(({ number, label }: { number: string; label: string }) => ({
        value: number,
        label: label ? `${label}: ${number}` : number,
      }));
    }, [organization?.phoneLabels]);

    const enduserFieldOptions = useMemo(() => {
      const builtInFields = Object.entries(ALL_ENDUSER_FIELDS_TO_DISPLAY_NAME).map(([key, displayName]) => ({
        value: key,
        label: displayName,
      }));

      const customFields = organization?.settings?.endusers?.customFields ?? [];

      const customFieldOptions = customFields.map((cf) => ({
        value: `custom.${cf.field}`,
        label: cf.field,
      }));

      return [...builtInFields, ...customFieldOptions];
    }, [organization?.settings?.endusers?.customFields]);

    const availableEnduserFieldOptions = useMemo(
      () => enduserFieldOptions.filter((opt) => !form.values.enduserFields.includes(opt.value)),
      [enduserFieldOptions, form.values.enduserFields]
    );

    const getEnduserFieldLabel = useCallback(
      (fieldValue: string) => {
        const option = enduserFieldOptions.find((opt) => opt.value === fieldValue);
        return option?.label ?? fieldValue;
      },
      [enduserFieldOptions]
    );
    const titleAndSubmitLabel = editMode ? t("Update Queue") : t("Add Queue");
    const trackingId = editMode ? "ticket-queues-edit-queue-submit" : "ticket-queues-create-queue-submit";

    return (
      <Sheet
        open={open}
        onOpenChange={(newVal) => {
          if (!newVal) {
            form.resetForm();
          }
          onOpenChange(newVal);
        }}
      >
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{titleAndSubmitLabel}</SheetTitle>
          </SheetHeader>
          <div className="grid flex-1 auto-rows-min gap-4 px-4 overflow-y-auto overflow-x-hidden w-full">
            <div className="space-y-2">
              <Label htmlFor="queue-title">{t("Title")}</Label>
              <Input id="queue-title" {...form.getFieldProps("title")} placeholder={t("Queue title")} />
            </div>
            <div className="space-y-2">
              <Label>{t("Users")}</Label>
              <UserSearchCombobox
                multiple
                value={selectedUsers}
                onChange={(newVal) =>
                  form.setFieldValue(
                    "userIds",
                    newVal.map(({ id }) => id)
                  )
                }
                placeholder={t("Select users...")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("Prevent Pulling Tickets")}</Label>
              <UserSearchCombobox
                multiple
                value={selectedPreventPullUsers}
                onChange={(newVal) => {
                  form.setFieldValue(
                    "preventPull",
                    newVal.map(({ id }) => id)
                  );
                }}
                placeholder={t("Select users...")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("Type")}</Label>
              <Select value={form.values.type} onValueChange={(value) => form.setFieldValue("type", value)}>
                <Select.Trigger className="w-full">
                  <Select.Value placeholder={t("Select type...")} />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="Default">{t("Default")}</Select.Item>
                  <Select.Item value="Autodialer">{t("Autodialer")}</Select.Item>
                </Select.Content>
              </Select>
            </div>
            {form.values.type === "Autodialer" && (
              <div className="space-y-2">
                <Label>{t("Default Phone Number")}</Label>
                <Select
                  value={form.values.defaultFromNumber}
                  onValueChange={(value) => form.setFieldValue("defaultFromNumber", value)}
                >
                  <Select.Trigger className="w-full">
                    <Select.Value placeholder={t("Select phone number...")} />
                  </Select.Trigger>
                  <Select.Content>
                    {phoneOptions.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>{t("User for Ticket Reminders")}</Label>
              <UserSearchCombobox
                value={selectedReminderUser}
                onChange={(newVal) => {
                  form.setFieldValue("overdueReminderUserId", newVal[0]?.id ?? "");
                }}
                placeholder={t("Select user...")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("Patient Field Context")}</Label>
              {form.values.enduserFields.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {form.values.enduserFields.map((field: string) => (
                    <Badge key={field} variant="secondary" className="gap-1">
                      {getEnduserFieldLabel(field)}
                      <button
                        type="button"
                        onClick={() => {
                          form.setFieldValue(
                            "enduserFields",
                            form.values.enduserFields.filter((f: string) => f !== field)
                          );
                        }}
                        className="ml-1 rounded-full hover:bg-muted-foreground/20"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <Combobox
                options={availableEnduserFieldOptions}
                value={undefined}
                onChange={(option: ComboboxOption | undefined) => {
                  if (option && option.value && !form.values.enduserFields.includes(option.value)) {
                    form.setFieldValue("enduserFields", [...form.values.enduserFields, option.value]);
                  }
                }}
                placeholder={t("Search fields...")}
                searchPlaceholder={t("Search...")}
                emptyText={t("No fields found")}
              />
            </div>
          </div>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="outline">{t("Cancel")}</Button>
            </SheetClose>
            <Button
              onClick={handleSubmit}
              disabled={editMode ? !form.isDirty || !form.isValid : !form.isValid}
              loading={isPending}
              data-trackingid={trackingId}
            >
              {titleAndSubmitLabel}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  },
  "queueId"
);
